import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { crmApi, type ApiRecord } from '../../lib/api';

type BusinessUnit = ApiRecord & {
	id: number;
	name: string;
	open_count?: number;
	won_count?: number;
	uses_defaults?: boolean;
};

type PipelineColumn = {
	stage: ApiRecord & { id: number; name: string; color?: string | null; is_won?: boolean; is_lost?: boolean };
	opportunities: ApiRecord[];
	total: number;
	amount_total: number;
};

function currency(n: number) {
	if (!n) return '$0';
	return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function PipelinesPage() {
	const [search, setSearch] = useSearchParams();
	const businessUnitsQuery = useQuery({ queryKey: ['business-units'], queryFn: crmApi.businessUnits });
	const units = (businessUnitsQuery.data ?? []) as BusinessUnit[];

	const selectedId = useMemo(() => {
		const qp = search.get('bu');
		if (qp) return qp;
		return units[0]?.id != null ? String(units[0].id) : '';
	}, [search, units]);

	const pipelineQuery = useQuery({
		queryKey: ['business-unit-pipeline', selectedId],
		queryFn: () => crmApi.businessUnitPipeline(selectedId),
		enabled: Boolean(selectedId)
	});

	const selected = units.find((u) => String(u.id) === selectedId);
	const columns = (pipelineQuery.data?.columns ?? []) as PipelineColumn[];

	return (
		<div className="space-y-3">
			<div className="rounded-2xl border border-subtle bg-surface-2 px-4 py-3 shadow-panel">
				<p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-accent">Pipelines</p>
				<h1 className="mt-1 text-xl font-semibold text-primary">Each business unit has its own pipeline</h1>
				<p className="mt-0.5 text-xs text-muted">A company can sit in many pipelines at once — the deals are independent.</p>
				<div className="mt-3 flex flex-wrap gap-1.5">
					{businessUnitsQuery.isLoading ? <span className="text-xs text-muted">Loading business units…</span> : null}
					{units.length === 0 && !businessUnitsQuery.isLoading ? (
						<span className="text-xs text-muted">No business units yet. Add vendors in Admin to create pipelines.</span>
					) : null}
					{units.map((u) => {
						const active = String(u.id) === selectedId;
						return (
							<button
								key={String(u.id)}
								type="button"
								onClick={() => setSearch({ bu: String(u.id) }, { replace: true })}
								className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs transition ${
									active ? 'bg-accent text-accent-contrast shadow-panel' : 'border border-subtle bg-surface-2 text-secondary hover:bg-surface-hover'
								}`}
							>
								<Building2 className="h-3.5 w-3.5" />
								<span>{u.name}</span>
								<span className={`rounded-full px-1.5 py-0.5 text-[10px] ${active ? 'bg-surface-2 text-accent' : 'bg-surface-hover text-secondary'}`}>
									{u.open_count ?? 0}
								</span>
							</button>
						);
					})}
				</div>
			</div>

			{selected ? (
				<div className="flex gap-3 overflow-x-auto pb-2">
					{columns.length === 0 && pipelineQuery.isLoading ? (
						<div className="text-sm text-muted">Loading pipeline…</div>
					) : null}
					{columns.map((col) => {
						const tone = col.stage.is_won
							? 'border-emerald-400/30 bg-emerald-500/5'
							: col.stage.is_lost
								? 'border-danger/30 bg-danger/5'
								: 'border-subtle bg-surface-2';
						return (
							<div key={String(col.stage.id)} className={`w-72 shrink-0 rounded-2xl border ${tone} px-3 py-3`}>
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: (col.stage.color as string) || '#64748b' }} />
										<p className="text-sm font-semibold text-primary">{col.stage.name}</p>
									</div>
									<span className="rounded-full bg-surface-hover px-2 py-0.5 text-[10px] text-secondary">{col.total}</span>
								</div>
								<p className="mt-0.5 text-[11px] text-muted">{currency(col.amount_total)}</p>
								<div className="mt-2 space-y-1.5">
									{col.opportunities.length === 0 ? (
										<div className="rounded-xl border border-dashed border-subtle px-3 py-3 text-[11px] text-muted">No deals in this stage.</div>
									) : (
										col.opportunities.map((o) => (
											<div key={String(o.id)} className="rounded-xl border border-subtle bg-canvas/70 px-3 py-2">
												<p className="truncate text-sm font-medium text-primary">{String(o.title ?? `#${o.id}`)}</p>
												<p className="mt-0.5 truncate text-[11px] text-muted">
													{String((o.company as ApiRecord | null)?.name ?? 'No company')}
												</p>
												{o.amount != null ? (
													<p className="mt-0.5 text-[11px] text-accent">{currency(Number(o.amount))}</p>
												) : null}
											</div>
										))
									)}
								</div>
							</div>
						);
					})}
				</div>
			) : null}
		</div>
	);
}
